import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useAppContext } from '../../context/AppContext';
import { Tooltip, EmptyState } from '../Common/ReusableUI';
import { ListSkeleton } from '../Common/SkeletonLoaders';
import { useToast } from '../Common/ToastProvider';
import { useConfirm } from '../Common/ConfirmDialog';
import {
  Settings as SettingsIcon, Search,
  CheckCircle, Clock,
  RefreshCw, Bell,
  Shield, Database, Server, Lock,
  Download, Building2,
  DoorOpen, UserCog, UserCheck, DollarSign, Link2, Sliders,
  AlertCircle, SearchX
} from 'lucide-react';
import FacilityManagement from './FacilityManagement';
import UserManagement from './UserManagement';
import RoomManagement from './RoomManagement';
import RoleManagement from './RoleManagement';
import NomineeManagement from './NomineeManagement';
import ShadowLedgerManagement from './ShadowLedgerManagement';
import ProviderRelationshipManagement from './ProviderRelationshipManagement';
import ConfigManagement from './ConfigManagement';

const SETTING_TOOLTIPS = {
  passwordPolicy: 'Determines minimum password length, complexity, and rotation requirements',
  sessionTimeout: 'Automatically logs out inactive users after the selected duration for security',
  emailNotifications: 'Sends email alerts for medication reminders, incidents, and care plan updates',
  pushNotifications: 'Browser and mobile push notifications for real-time clinical alerts',
  alertFrequency: 'Controls how often grouped notification digests are delivered',
  soundAlerts: 'Plays an audible tone for high-priority clinical and safety alerts',
  dataRetention: 'How long audit logs, historical data, and archived records are kept before purging',
  twoFactor: 'Requires a second authentication step (SMS or authenticator app) at login',
  loginHistory: 'Records IP address, device, and timestamp for every login attempt',
  logLevel: 'Controls verbosity of system logs — higher levels may impact storage',
};

const buildDefaultSettings = () => ({
  theme: 'Light',
  language: 'English',
  timezone: 'Australia/Melbourne',
  dateFormat: 'DD/MM/YYYY',
  twoFactor: 'Enabled',
  sessionTimeout: '30 minutes',
  passwordPolicy: 'Strong',
  loginHistory: 'Enabled',
  emailNotifications: 'Enabled',
  pushNotifications: 'Enabled',
  alertFrequency: 'Immediate',
  soundAlerts: 'Enabled',
  dataSharing: 'Limited',
  analytics: 'Enabled',
  cookies: 'Essential Only',
  dataRetention: '1 year',
  autoBackup: 'Daily',
  maintenanceMode: 'Disabled',
  debugMode: 'Disabled',
  logLevel: 'Info',
});

const Settings = () => {
  const { state } = useAppContext();
  const { deviceInfo } = state;
  const { optimization } = deviceInfo;
  const { showToast } = useToast();
  const confirm = useConfirm();

  const [activeTab, setActiveTab] = useState('general');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [isSubModuleLoading, setIsSubModuleLoading] = useState(false);
  const [settingsValues, setSettingsValues] = useState(buildDefaultSettings);
  const [savedSnapshot, setSavedSnapshot] = useState(buildDefaultSettings);

  const hasUnsavedChanges = useMemo(
    () => JSON.stringify(settingsValues) !== JSON.stringify(savedSnapshot),
    [settingsValues, savedSnapshot]
  );

  useEffect(() => {
    const subModuleTabs = ['facilities', 'rooms', 'users', 'roles', 'nominees', 'shadow-ledger', 'providers', 'config'];
    if (subModuleTabs.includes(activeTab)) {
      setIsSubModuleLoading(true);
      const timer = setTimeout(() => setIsSubModuleLoading(false), 500);
      return () => clearTimeout(timer);
    }
  }, [activeTab]);

  const settingsCategories = useMemo(() => [
    {
      id: 'general',
      label: 'General',
      icon: SettingsIcon,
      description: 'Basic system settings and preferences',
      settings: [
        { id: 'theme', label: 'Theme', type: 'select', options: ['Light', 'Dark', 'Auto'] },
        { id: 'language', label: 'Language', type: 'select', options: ['English', 'Spanish', 'French'] },
        { id: 'timezone', label: 'Timezone', type: 'select', options: ['Australia/Melbourne', 'Australia/Sydney', 'UTC'] },
        { id: 'dateFormat', label: 'Date Format', type: 'select', options: ['DD/MM/YYYY', 'MM/DD/YYYY', 'YYYY-MM-DD'] }
      ]
    },
    {
      id: 'security',
      label: 'Security',
      icon: Shield,
      description: 'Security and authentication settings',
      settings: [
        { id: 'twoFactor', label: 'Two-Factor Authentication', type: 'toggle' },
        { id: 'sessionTimeout', label: 'Session Timeout', type: 'select', options: ['15 minutes', '30 minutes', '1 hour', '4 hours'] },
        { id: 'passwordPolicy', label: 'Password Policy', type: 'select', options: ['Basic', 'Strong', 'Very Strong'] },
        { id: 'loginHistory', label: 'Login History', type: 'toggle' }
      ]
    },
    {
      id: 'notifications',
      label: 'Notifications',
      icon: Bell,
      description: 'Notification preferences and alerts',
      settings: [
        { id: 'emailNotifications', label: 'Email Notifications', type: 'toggle' },
        { id: 'pushNotifications', label: 'Push Notifications', type: 'toggle' },
        { id: 'alertFrequency', label: 'Alert Frequency', type: 'select', options: ['Immediate', 'Hourly', 'Daily', 'Weekly'] },
        { id: 'soundAlerts', label: 'Sound Alerts', type: 'toggle' }
      ]
    },
    {
      id: 'privacy',
      label: 'Privacy',
      icon: Lock,
      description: 'Privacy and data protection settings',
      settings: [
        { id: 'dataSharing', label: 'Data Sharing', type: 'select', options: ['None', 'Limited', 'Full'] },
        { id: 'analytics', label: 'Analytics Collection', type: 'toggle' },
        { id: 'cookies', label: 'Cookie Preferences', type: 'select', options: ['Essential Only', 'Functional', 'All'] },
        { id: 'dataRetention', label: 'Data Retention', type: 'select', options: ['30 days', '6 months', '1 year', '3 years'] }
      ]
    },
    {
      id: 'system',
      label: 'System',
      icon: Server,
      description: 'System configuration and maintenance',
      settings: [
        { id: 'autoBackup', label: 'Auto Backup', type: 'select', options: ['Disabled', 'Daily', 'Weekly', 'Monthly'] },
        { id: 'maintenanceMode', label: 'Maintenance Mode', type: 'toggle' },
        { id: 'debugMode', label: 'Debug Mode', type: 'toggle' },
        { id: 'logLevel', label: 'Log Level', type: 'select', options: ['Error', 'Warning', 'Info', 'Debug'] }
      ]
    },
    { id: 'facilities', label: 'Facilities', icon: Building2, description: 'Manage facilities, NDIS types, Aged Care types, and Business Groups', settings: [] },
    { id: 'rooms', label: 'Rooms & Beds', icon: DoorOpen, description: 'Manage rooms, beds, and occupancy across facilities and wards', settings: [] },
    { id: 'users', label: 'Users', icon: UserCog, description: 'Create and manage user accounts, passwords, and access', settings: [] },
    { id: 'roles', label: 'Roles & Permissions', icon: Shield, description: 'View and manage role-based access control', settings: [] },
    { id: 'nominees', label: 'NDIS Nominees', icon: UserCheck, description: 'Manage plan nominees, correspondence nominees, and court-appointed guardians', settings: [] },
    { id: 'shadow-ledger', label: 'Shadow Ledger', icon: DollarSign, description: 'Track NDIS budget balances and funding at risk', settings: [] },
    { id: 'providers', label: 'Provider Relationships', icon: Link2, description: 'Manage NDIS provider relationships and consent scopes', settings: [] },
    { id: 'config', label: 'Configuration', icon: Sliders, description: 'Manage shift types, note templates, payment methods, and billing categories', settings: [] },
  ], []);

  const getSettingValue = useCallback((setting) => {
    const val = settingsValues[setting.id];
    if (setting.type === 'toggle') return val === 'Enabled';
    return val;
  }, [settingsValues]);

  const handleSettingChange = useCallback((categoryId, settingId, value) => {
    const category = settingsCategories.find(c => c.id === categoryId);
    const setting = category?.settings.find(s => s.id === settingId);
    const displayValue = setting?.type === 'toggle' ? (value ? 'Enabled' : 'Disabled') : value;

    setSettingsValues(prev => ({ ...prev, [settingId]: displayValue }));

    const friendlyLabel = setting?.label || settingId;
    showToast(`${friendlyLabel} updated to ${displayValue}`, 'success', 3000);

    if (process.env.NODE_ENV === 'development') {
      console.debug('[Settings] changed', { categoryId, settingId, value: displayValue });
    }
  }, [settingsCategories, showToast]);

  const handleSave = useCallback(() => {
    setSavedSnapshot({ ...settingsValues });
    showToast('All settings saved', 'success');
  }, [settingsValues, showToast]);

  const handleReset = useCallback(async () => {
    const confirmed = await confirm({
      title: 'Reset all settings',
      message: 'Reset all settings to defaults? This cannot be undone.',
      confirmLabel: 'Reset',
      cancelLabel: 'Cancel',
      variant: 'danger',
    });
    if (confirmed) {
      const defaults = buildDefaultSettings();
      setSettingsValues(defaults);
      setSavedSnapshot(defaults);
      showToast('Settings restored to defaults', 'warning');
    }
  }, [confirm, showToast]);

  const filteredCategories = useMemo(() => settingsCategories.filter(category => {
    const matchesSearch = category.label.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         category.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         category.settings.some(s => s.label.toLowerCase().includes(searchQuery.toLowerCase()));
    const matchesCategory = filterCategory === 'all' || category.id === filterCategory;
    return matchesSearch && matchesCategory;
  }), [settingsCategories, searchQuery, filterCategory]);

  const renderSettingRow = useCallback((setting, categoryId) => {
    const tooltip = SETTING_TOOLTIPS[setting.id];
    const label = (
      <div className="flex-1">
        <h3 className="font-medium text-gray-800">{setting.label}</h3>
        <p className="text-sm text-gray-600 mt-1">
          {setting.type === 'toggle' ? 'Enable or disable this feature' : 'Select your preference'}
        </p>
      </div>
    );

    const control = setting.type === 'toggle' ? (
      <button
        onClick={() => handleSettingChange(categoryId, setting.id, !getSettingValue(setting))}
        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
          getSettingValue(setting) ? 'bg-blue-600' : 'bg-gray-200'
        }`}
        role="switch"
        aria-checked={getSettingValue(setting)}
      >
        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
          getSettingValue(setting) ? 'translate-x-6' : 'translate-x-1'
        }`} />
      </button>
    ) : (
      <select
        value={getSettingValue(setting)}
        onChange={(e) => handleSettingChange(categoryId, setting.id, e.target.value)}
        className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-transparent"
      >
        {setting.options.map((option) => (
          <option key={option} value={option}>{option}</option>
        ))}
      </select>
    );

    return (
      <div key={setting.id} className="border border-gray-200 rounded-lg p-4">
        <div className="flex justify-between items-center">
          {tooltip ? (
            <Tooltip content={tooltip}>
              {label}
            </Tooltip>
          ) : label}
          {control}
        </div>
      </div>
    );
  }, [handleSettingChange, getSettingValue]);

  const renderCategorySettings = useCallback((categoryId) => {
    const category = settingsCategories.find(c => c.id === categoryId);
    if (!category || category.settings.length === 0) return null;
    return (
      <div className="space-y-4">
        <div className="mb-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-2">{category.label}</h2>
          <p className="text-gray-600">{category.description}</p>
        </div>
        {category.settings.map((setting) => renderSettingRow(setting, categoryId))}
      </div>
    );
  }, [settingsCategories, renderSettingRow]);

  const inlineSettingTabs = ['general', 'security', 'notifications', 'privacy', 'system'];
  const isSearchEmpty = searchQuery.trim() !== '' && filteredCategories.length === 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-gray-600 to-slate-600 rounded-2xl p-6 text-white shadow-xl min-h-[120px] flex items-center">
        <div className="flex justify-between items-center w-full">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-3">
              Settings & Configuration
              <SettingsIcon className="w-8 h-8 text-gray-300 animate-pulse" />
              {hasUnsavedChanges && (
                <span className="ml-2 inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-amber-400 text-amber-900 animate-pulse">
                  <AlertCircle className="w-3 h-3" />
                  Unsaved changes
                </span>
              )}
            </h1>
            <p className="text-base mt-2 opacity-90">
              Manage system settings and user preferences
            </p>
          </div>
          <div className="flex items-center gap-2">
            {hasUnsavedChanges && (
              <button
                onClick={handleSave}
                className="bg-emerald-500 text-white px-4 py-2 rounded-xl font-semibold hover:bg-emerald-600 transition-colors flex items-center gap-2 text-sm"
              >
                <CheckCircle className="w-4 h-4" />
                Save
              </button>
            )}
            <button 
              onClick={() => {
                showToast('Settings exported to clipboard', 'info');
              }}
              className={`bg-white text-gray-600 ${optimization.buttonSize} rounded-xl font-semibold hover:bg-opacity-90 ${optimization.animationSpeed} flex items-center gap-2`}
            >
              <Download className={`${optimization.iconSize}`} />
              Export Settings
            </button>
          </div>
        </div>
      </div>

      {/* Settings Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-200 h-[140px] flex flex-col justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <SettingsIcon className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h3 className="font-semibold text-lg">{settingsCategories.length}</h3>
              <p className="text-sm text-gray-600">Settings Categories</p>
              <div className="flex items-center gap-1 text-xs">
                <CheckCircle className="w-3 h-3 text-green-600" />
                <span className="text-green-600">Configured</span>
              </div>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-200 h-[140px] flex flex-col justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <Shield className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <h3 className="font-semibold text-lg">Secure</h3>
              <p className="text-sm text-gray-600">Security Status</p>
              <div className="flex items-center gap-1 text-xs">
                <CheckCircle className="w-3 h-3 text-green-600" />
                <span className="text-green-600">Protected</span>
              </div>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-200 h-[140px] flex flex-col justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Bell className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <h3 className="font-semibold text-lg">Active</h3>
              <p className="text-sm text-gray-600">Notifications</p>
              <div className="flex items-center gap-1 text-xs">
                <span className="text-purple-600">Enabled</span>
              </div>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-200 h-[140px] flex flex-col justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-orange-100 rounded-lg">
              <Database className="w-5 h-5 text-orange-600" />
            </div>
            <div>
              <h3 className="font-semibold text-lg">2.4 GB</h3>
              <p className="text-sm text-gray-600">Storage Used</p>
              <div className="flex items-center gap-1 text-xs">
                <span className="text-orange-600">45% of total</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="bg-white rounded-xl shadow-sm">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6 overflow-x-auto">
            {settingsCategories.map((category) => {
              const Icon = category.icon;
              return (
                <button
                  key={category.id}
                  onClick={() => setActiveTab(category.id)}
                  className={`flex items-center gap-2 py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                    activeTab === category.id
                      ? 'border-blue-600 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {category.label}
                </button>
              );
            })}
          </nav>
        </div>

        <div className="p-6">
          {/* Search and Filters */}
          <div className="flex flex-col md:flex-row gap-4 mb-6">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search settings..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-transparent"
              />
            </div>
            <div className="flex gap-2">
              <select
                value={filterCategory}
                onChange={(e) => setFilterCategory(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-transparent"
              >
                <option value="all">All Categories</option>
                {settingsCategories.map(category => (
                  <option key={category.id} value={category.id}>
                    {category.label}
                  </option>
                ))}
              </select>
              <button 
                onClick={handleReset}
                className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <RefreshCw className="w-4 h-4" />
                Reset
              </button>
            </div>
          </div>

          {/* Empty state for search */}
          {isSearchEmpty && (
            <EmptyState
              icon={SearchX}
              title="No settings found"
              description={`No settings match "${searchQuery}". Try a different search term.`}
              action={
                <button
                  onClick={() => setSearchQuery('')}
                  className="mt-2 px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
                >
                  Clear search
                </button>
              }
            />
          )}

          {/* Inline settings tabs */}
          {!isSearchEmpty && inlineSettingTabs.includes(activeTab) && renderCategorySettings(activeTab)}

          {/* Sub-module tabs with skeleton loading */}
          {!isSearchEmpty && activeTab === 'facilities' && (
            isSubModuleLoading ? <ListSkeleton count={4} /> : <FacilityManagement />
          )}
          {!isSearchEmpty && activeTab === 'rooms' && (
            isSubModuleLoading ? <ListSkeleton count={4} /> : <RoomManagement />
          )}
          {!isSearchEmpty && activeTab === 'users' && (
            isSubModuleLoading ? <ListSkeleton count={4} /> : <UserManagement />
          )}
          {!isSearchEmpty && activeTab === 'roles' && (
            isSubModuleLoading ? <ListSkeleton count={4} /> : <RoleManagement />
          )}
          {!isSearchEmpty && activeTab === 'nominees' && (
            isSubModuleLoading ? <ListSkeleton count={4} /> : <NomineeManagement />
          )}
          {!isSearchEmpty && activeTab === 'shadow-ledger' && (
            isSubModuleLoading ? <ListSkeleton count={4} /> : <ShadowLedgerManagement />
          )}
          {!isSearchEmpty && activeTab === 'providers' && (
            isSubModuleLoading ? <ListSkeleton count={4} /> : <ProviderRelationshipManagement />
          )}
          {!isSearchEmpty && activeTab === 'config' && (
            isSubModuleLoading ? <ListSkeleton count={4} /> : <ConfigManagement />
          )}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <button
          type="button"
          onClick={() => showToast('Settings exported to clipboard', 'info')}
          className="bg-white rounded-2xl p-6 shadow-lg border border-gray-200 h-[140px] flex flex-col justify-between text-left hover:shadow-xl transition-shadow"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Download className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h3 className="font-semibold">Export Settings</h3>
              <p className="text-sm text-gray-600">Backup configuration</p>
            </div>
          </div>
        </button>
        
        <button
          type="button"
          onClick={handleReset}
          className="bg-white rounded-2xl p-6 shadow-lg border border-gray-200 h-[140px] flex flex-col justify-between text-left hover:shadow-xl transition-shadow"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <RefreshCw className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <h3 className="font-semibold">Reset to Default</h3>
              <p className="text-sm text-gray-600">Restore defaults</p>
            </div>
          </div>
        </button>
        
        <button
          type="button"
          onClick={() => showToast('Security check passed — all systems nominal', 'success')}
          className="bg-white rounded-2xl p-6 shadow-lg border border-gray-200 h-[140px] flex flex-col justify-between text-left hover:shadow-xl transition-shadow"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Shield className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <h3 className="font-semibold">Security Check</h3>
              <p className="text-sm text-gray-600">Verify settings</p>
            </div>
          </div>
        </button>
      </div>

      {/* Sticky unsaved-changes footer */}
      {hasUnsavedChanges && (
        <div className="fixed bottom-0 left-0 right-0 z-50 bg-amber-50 border-t border-amber-300 px-6 py-3 flex items-center justify-between shadow-[0_-4px_12px_rgba(0,0,0,0.08)]">
          <div className="flex items-center gap-2 text-amber-800 text-sm font-medium">
            <AlertCircle className="w-4 h-4" />
            You have unsaved changes
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleReset}
              className="px-3 py-1.5 text-xs font-medium rounded-lg border border-amber-300 text-amber-700 hover:bg-amber-100 transition-colors"
            >
              Discard
            </button>
            <button
              onClick={handleSave}
              className="px-4 py-1.5 text-xs font-semibold rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 transition-colors"
            >
              Save changes
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings;
